import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Center,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  chakra,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';

import {
  SendBulkEmailInvitationsV3Input,
} from '@/graphql-types/globalTypes';
import { logError } from '@/imports/logging/ClientLogger';

import {
  MembersInviteModalV3_AggregateInvitationStatusForUsersV3,
  MembersInviteModalV3_AggregateInvitationStatusForUsersV3Variables,
} from './graphql-types/MembersInviteModalV3_AggregateInvitationStatusForUsersV3';
import {
  membersInviteModalV3_sendBulkEmailInvitationsV3,
  membersInviteModalV3_sendBulkEmailInvitationsV3Variables,
} from './graphql-types/membersInviteModalV3_sendBulkEmailInvitationsV3';

const AGGREGATE_INVITATION_STATUS_USERS_QUERY = gql`
  query MembersInviteModalV3_AggregateInvitationStatusForUsersV3(
    $input: AggregateInvitationStatusForUsersV3Input!
  ) {
    aggregateInvitationStatusForUsersV3(input: $input) {
      numInvited
      numNotInvited
      numActive
      numDeactivated
    }
  }
`;

const SEND_BULK_EMAIL_INVITATIONS = gql`
  mutation membersInviteModalV3_sendBulkEmailInvitationsV3(
    $input: SendBulkEmailInvitationsV3Input!
  ) {
    sendBulkEmailInvitationsV3(input: $input) {
      success
    }
  }
`;

type IMembersInviteModalProps = {
  onClose: () => void;
  usersSelection: Pick<SendBulkEmailInvitationsV3Input, 'userIds' | 'filter'>;
};

const correctPhrasing = (value: number): string => {
  return value === 1 ? '1 person' : `${value} people`;
};

export const MembersInviteModal: React.FC<IMembersInviteModalProps> = ({
  onClose,
  usersSelection,
}) => {
  const toast = useToast();

  const aggregateInvitationStatusQuery = useQuery<
    MembersInviteModalV3_AggregateInvitationStatusForUsersV3,
    MembersInviteModalV3_AggregateInvitationStatusForUsersV3Variables
  >(AGGREGATE_INVITATION_STATUS_USERS_QUERY, {
    variables: {
      input: usersSelection,
    },
  });

  const [sendInvitationEmails, { loading: loadingSendInvitation }] = useMutation<
    membersInviteModalV3_sendBulkEmailInvitationsV3,
    membersInviteModalV3_sendBulkEmailInvitationsV3Variables
  >(SEND_BULK_EMAIL_INVITATIONS);

  const aggregateCounts =
    aggregateInvitationStatusQuery.data?.aggregateInvitationStatusForUsersV3;

  const newInvites = aggregateCounts?.numNotInvited ?? 0;
  const resendInvites = aggregateCounts?.numInvited ?? 0;
  const skippedInvites = aggregateCounts?.numActive ?? 0;
  const deactivatedUsers = aggregateCounts?.numDeactivated ?? 0;
  const invitesToSend = newInvites + resendInvites;

  const handleSendInvites = async () => {
    const input: SendBulkEmailInvitationsV3Input = {
      ...usersSelection,
    };

    try {
      await sendInvitationEmails({
        variables: { input },
      });
      toast({ status: 'success', description: 'Sending invitation emails' });
      onClose();
    } catch (err) {
      toast({ status: 'error', description: 'Failed to send' });
      logError(err);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      {aggregateInvitationStatusQuery.loading ? (
        <ModalContent>
          <Center py="16">
            <Spinner />
          </Center>
        </ModalContent>
      ) : aggregateInvitationStatusQuery.error ? (
        <ModalContent>
          <ModalHeader border="1px solid" borderColor="strokePrimary.500">
            Send bulk invite
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py="6">
            <Text color="textSecondary.500">
              Unable to load invite details. Please try again.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" borderColor="primary.500" onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      ) : (
        <ModalContent>
          <ModalHeader border="1px solid" borderColor="strokePrimary.500">
            Send bulk invite
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py="6">
            <Stack spacing="4">
              <Text fontSize="md" color="textSecondary.500" lineHeight="tall">
                Are you sure you want to send invite emails to the selected
                contacts? They will receive an email with instructions to join
                the community using your default invite template.
              </Text>

              <Stack spacing="1">
                {newInvites > 0 && (
                  <Text fontSize="sm" color="textSecondary.500">
                    <chakra.strong>{correctPhrasing(newInvites)}</chakra.strong>{' '}
                    will receive an invite for the first time
                  </Text>
                )}
                {resendInvites > 0 && (
                  <Text fontSize="sm" color="textSecondary.500">
                    <chakra.strong>{correctPhrasing(resendInvites)}</chakra.strong>{' '}
                    will be resent an invite
                  </Text>
                )}
                {skippedInvites + deactivatedUsers > 0 && (
                  <Text fontSize="sm" color="textSecondary.500">
                    <chakra.strong>
                      {correctPhrasing(skippedInvites + deactivatedUsers)}
                    </chakra.strong>{' '}
                    won&apos;t be sent an invite because they are already active
                    or have been deactivated
                  </Text>
                )}
              </Stack>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="outline"
              borderColor="primary.500"
              mr={3}
              onClick={onClose}
              isDisabled={loadingSendInvitation}
            >
              Cancel
            </Button>
            <Button
              colorScheme="primary"
              isLoading={loadingSendInvitation}
              loadingText="Sending"
              isDisabled={invitesToSend < 1}
              onClick={handleSendInvites}
            >
              Send {invitesToSend} Invite{invitesToSend === 1 ? '' : 's'}
            </Button>
          </ModalFooter>
        </ModalContent>
      )}
    </Modal>
  );
};
